import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import DatePicker from "react-multi-date-picker";
import "react-multi-date-picker/styles/colors/teal.css";
import "./PropertyDetails.css";
import { Helmet } from "react-helmet-async";

// ✅ Lucide icons (Airbnb-ish)
import {
  Wifi,
  ParkingCircle,
  Waves,
  Snowflake,
  UtensilsCrossed,
  Tv,
  WashingMachine,
  Flame,
  ArrowUpDown,
  Sailboat,
  Tag,
  Users,
  BedDouble,
  Bath,
  Share2,
} from "lucide-react";

/* ✅ Use one API base like App.js */
const API_URL =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_API_URL || "https://api.hellastay.com"
    : "http://localhost:8000";

/* ===================== Date Helpers (timezone-safe) ===================== */
function toISO(value) {
  try {
    if (!value) return null;

    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }

    if (typeof value?.format === "function") return value.format("YYYY-MM-DD");

    if (value instanceof Date) {
      if (isNaN(value.getTime())) return null;
      const y = value.getFullYear();
      const m = String(value.getMonth() + 1).padStart(2, "0");
      const d = String(value.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    }

    const dt = new Date(value);
    if (isNaN(dt.getTime())) return null;
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const d = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  } catch {
    return null;
  }
}

function isoToDate(iso) {
  try {
    const s = String(iso || "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
    const [y, m, d] = s.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    if (isNaN(dt.getTime())) return null;
    return dt;
  } catch {
    return null;
  }
}

function normalizeDateArray(values) {
  const arr = Array.isArray(values) ? values : values ? [values] : [];
  const unique = Array.from(new Set(arr.map(toISO).filter(Boolean)));
  unique.sort((a, b) => a.localeCompare(b));
  return unique;
}

function getMonthKeyFromISO(iso) {
  try {
    const s = String(iso || "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
    const month = Number(s.slice(5, 7));
    if (!Number.isFinite(month) || month < 1 || month > 12) return null;
    return String(month);
  } catch {
    return null;
  }
}

function formatReviewDate(value) {
  try {
    if (!value) return "";
    const dt = new Date(value);
    if (isNaN(dt.getTime())) return "";
    return dt.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

/* ✅ supports both old string images and new Cloudinary {url, publicId} */
function imgUrl(img) {
  if (!img) return "";
  if (typeof img === "string") return img;
  if (typeof img === "object" && typeof img.url === "string") return img.url;
  return "";
}

function titleCase(str) {
  const s = String(str || "").trim();
  if (!s) return "";
  return s
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function shortLocation(text) {
  const s = String(text || "").trim();
  if (!s) return "";
  const parts = s
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);

  if (parts.length <= 2) return s;

  const last = parts[parts.length - 1];
  const country = /greece|bulgaria|turkey|cyprus/i.test(last) ? last : "";
  const first = parts[0];
  const second = parts[1];

  return country ? `${first}, ${second}, ${country}` : `${first}, ${second}`;
}

function clampText(s, max = 160) {
  const x = String(s || "").replace(/\s+/g, " ").trim();
  if (!x) return "";
  return x.length > max ? x.slice(0, max - 3) + "..." : x;
}

/* ===== small accordion ===== */
function Accordion({ title, open, onToggle, children }) {
  return (
    <div className="pd__accBox">
      <button type="button" className="pd__accHead" onClick={onToggle}>
        <span>{title}</span>
        <span style={{ opacity: 0.6, fontWeight: 900 }}>{open ? "–" : "+"}</span>
      </button>
      {open && <div className="pd__accBody">{children}</div>}
    </div>
  );
}

export default function PropertyDetails({ user, properties, setProperties }) {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const property = useMemo(() => {
    const pid = String(id);
    return (properties || []).find((p) => String(p?.id ?? p?._id) === pid) || null;
  }, [properties, id]);

  const propertyId = useMemo(() => {
    const pid = property?.id ?? property?._id ?? "";
    return String(pid || "");
  }, [property?.id, property?._id]);

  const [selectedDatesByRoom, setSelectedDatesByRoom] = useState([]);
  const [bookingsByRoomId, setBookingsByRoomId] = useState(new Map());

  const [guestName, setGuestName] = useState(user?.name || "");
  const [guestPhone, setGuestPhone] = useState(user?.phone || "");
  const [guestEmail, setGuestEmail] = useState(user?.email || "");
  const [guestGuests, setGuestGuests] = useState("1");
  const [guestMessage, setGuestMessage] = useState("");
  const [isReserving, setIsReserving] = useState(false);

  // ✅ IMPORTANT: Hooks MUST be before any conditional return
  const [showAllAmenities, setShowAllAmenities] = useState(false);
  const [openRules, setOpenRules] = useState(false);
  const [openExtras, setOpenExtras] = useState(false);

  // ✅ Description collapse
  const [isDescExpanded, setIsDescExpanded] = useState(false);

  // ✅ MOBILE Airbnb-style reserve sheet
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= 900 : false
  );
  const [isReserveSheetOpen, setIsReserveSheetOpen] = useState(false);

  // ✅ Reviews
  const [reviews, setReviews] = useState([]);
  const [reviewsCount, setReviewsCount] = useState(Number(property?.reviewsCount || 0));
  const [reviewAverage, setReviewAverage] = useState(Number(property?.rating || 0));
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewError, setReviewError] = useState("");
  const [reviewSuccess, setReviewSuccess] = useState("");

  // ✅ Stripe / payment
  const [stripePublishableKey, setStripePublishableKey] = useState("");
  const [stripeEnabled, setStripeEnabled] = useState(false);
  const [isLoadingStripeConfig, setIsLoadingStripeConfig] = useState(false);

  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [paymentSuccess, setPaymentSuccess] = useState("");
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentQueue, setPaymentQueue] = useState([]);
  const [currentPaymentBooking, setCurrentPaymentBooking] = useState(null);
  const [currentPaymentClientSecret, setCurrentPaymentClientSecret] = useState("");
  const [currentPaymentAmount, setCurrentPaymentAmount] = useState(0);

  const stripeInstanceRef = useRef(null);
  const stripeInstanceKeyRef = useRef("");
  const stripeElementsRef = useRef(null);

  const stripeCardNumberRef = useRef(null);
  const stripeCardExpiryRef = useRef(null);
  const stripeCardCvcRef = useRef(null);

  const stripeCardNumberMountRef = useRef(null);
  const stripeCardExpiryMountRef = useRef(null);
  const stripeCardCvcMountRef = useRef(null);

  const stripeScriptPromiseRef = useRef(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (user?.name) setGuestName(user.name);
    if (user?.phone) setGuestPhone(user.phone);
    if (user?.email) setGuestEmail(user.email);
  }, [user?.name, user?.phone, user?.email]);

  useEffect(() => {
    setIsDescExpanded(false);
  }, [propertyId]);

  useEffect(() => {
    setReviewRating(5);
    setReviewComment("");
    setReviewError("");
    setReviewSuccess("");
  }, [propertyId]);

  // ✅ only when actual property changes, not on every parent re-render
  useEffect(() => {
    if (!propertyId) return;
    const rooms = Array.isArray(property?.rooms) ? property.rooms : [];
    setSelectedDatesByRoom(rooms.map((r) => ({ roomId: r.id, dates: [] })));
  }, [propertyId]);

  const images = useMemo(() => {
    const raw = Array.isArray(property?.images) ? property.images : [];
    const urls = raw.map((x) => imgUrl(x)).filter(Boolean);
    return urls.length ? urls : ["/placeholder.png"];
  }, [property?.images]);

  const locationTextValue = useMemo(() => {
    if (!property) return "";
    if (typeof property.location === "string") return property.location;
    if (property.location?.text) return property.location.text;
    return "";
  }, [property]);

  const minRoomPrice = useMemo(() => {
    if (!property) return 0;

    const getBaseRoomPrice = (room) => {
      const rp = Number(room?.price || 0);
      if (Number.isFinite(rp) && rp > 0) return rp;
      const pp = Number(property?.price || 0);
      return Number.isFinite(pp) ? pp : 0;
    };

    const getMinMonthlyOrBase = (room) => {
      const base = getBaseRoomPrice(room);
      const monthly = room?.monthlyPrices && typeof room.monthlyPrices === "object" ? room.monthlyPrices : {};
      const monthlyValues = Object.values(monthly)
        .map((v) => Number(v))
        .filter((n) => Number.isFinite(n) && n > 0);

      if (monthlyValues.length === 0) return base;
      return Math.min(base, ...monthlyValues);
    };

    const roomPrices = (property.rooms || [])
      .map((r) => getMinMonthlyOrBase(r))
      .filter((n) => Number.isFinite(n) && n > 0);

    if (roomPrices.length > 0) return Math.min(...roomPrices);

    const pp = Number(property.price || 0);
    return Number.isFinite(pp) ? pp : 0;
  }, [property]);

  const typeLabel = useMemo(() => {
    const tpe = String(property?.propertyType || "").toLowerCase();
    if (!tpe) return "";
    if (tpe === "apartment") return "Apartment";
    if (tpe === "hotel") return "Hotel";
    if (tpe === "villa") return "Villa";
    if (tpe === "room") return "Room";
    return tpe;
  }, [property?.propertyType]);

  const amenitiesObj = useMemo(() => {
    const a = property?.amenities;
    return a && typeof a === "object" ? a : null;
  }, [property?.amenities]);

  const selectedAmenities = useMemo(() => {
    if (!amenitiesObj) return [];

    const pairs = [
      ["wifi", "Wi-Fi", Wifi],
      ["parking", "Parking", ParkingCircle],
      ["pool", "Pool", Waves],
      ["ac", "Air conditioning", Snowflake],
      ["kitchen", "Kitchen", UtensilsCrossed],
      ["tv", "TV", Tv],
      ["washer", "Washer", WashingMachine],
      ["heating", "Heating", Flame],
      ["elevator", "Elevator", ArrowUpDown],
      ["seaView", "Sea view", Sailboat],
    ];

    return pairs
      .filter(([k]) => !!amenitiesObj[k])
      .map(([key, label, Icon]) => ({ key, label, Icon }));
  }, [amenitiesObj]);

  const rules = useMemo(() => {
    const r = property?.houseRules;
    return r && typeof r === "object" ? r : null;
  }, [property?.houseRules]);

  const feesObj = useMemo(() => {
    const f = property?.fees;
    return f && typeof f === "object" ? f : null;
  }, [property?.fees]);

  const discountsObj = useMemo(() => {
    const d = property?.discounts;
    return d && typeof d === "object" ? d : null;
  }, [property?.discounts]);

  const descText = useMemo(() => String(property?.description || "").trim(), [property?.description]);
  const shouldClampDesc = useMemo(() => {
    if (!descText) return false;
    const lines = descText.split(/\r?\n/).filter((x) => x.trim().length > 0).length;
    if (lines >= 7) return true;
    if (descText.length >= 420) return true;
    return false;
  }, [descText]);

  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [activeImgIndex, setActiveImgIndex] = useState(0);

  const openGallery = (idx = 0) => {
    setActiveImgIndex(idx);
    setIsGalleryOpen(true);
  };
  const closeGallery = () => setIsGalleryOpen(false);

  const prevImg = () => setActiveImgIndex((i) => (i - 1 + images.length) % images.length);
  const nextImg = () => setActiveImgIndex((i) => (i + 1) % images.length);

  useEffect(() => {
    if (!isGalleryOpen) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeGallery();
      if (e.key === "ArrowLeft") prevImg();
      if (e.key === "ArrowRight") nextImg();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isGalleryOpen, images.length]);

  useEffect(() => {
    if (!isReserveSheetOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isReserveSheetOpen]);

  useEffect(() => {
    if (!paymentModalOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [paymentModalOpen]);

  /* ===================== Reviews fetch ===================== */
  useEffect(() => {
    if (!property || !propertyId) return;

    let cancelled = false;

    const fallbackReviews = Array.isArray(property?.reviews) ? property.reviews : [];
    setReviews(fallbackReviews);
    setReviewsCount(Number(property?.reviewsCount || fallbackReviews.length || 0));
    setReviewAverage(Number(property?.rating || 0));

    fetch(`${API_URL}/api/property/${propertyId}/reviews`)
      .then(async (res) => {
        if (!res.ok) throw new Error("Failed to load reviews");
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;

        const incomingReviews = Array.isArray(data?.reviews) ? data.reviews : fallbackReviews;
        const avg = Number(data?.rating ?? data?.averageRating ?? property?.rating ?? 0);
        const count = Number(data?.reviewsCount ?? incomingReviews.length ?? 0);

        setReviews(incomingReviews);
        setReviewAverage(Number.isFinite(avg) ? avg : 0);
        setReviewsCount(Number.isFinite(count) ? count : 0);

        if (typeof setProperties === "function") {
          setProperties((prev) =>
            (prev || []).map((p) =>
              String(p?.id ?? p?._id) === propertyId
                ? {
                    ...p,
                    reviews: incomingReviews,
                    rating: Number.isFinite(avg) ? avg : 0,
                    reviewsCount: Number.isFinite(count) ? count : 0,
                  }
                : p
            )
          );
        }
      })
      .catch(() => {
        if (cancelled) return;
        setReviews(fallbackReviews);
      });

    return () => {
      cancelled = true;
    };
  }, [property, propertyId, setProperties]);

  /* ===================== Stripe helpers ===================== */
  const loadStripeConfig = async () => {
    if (!user) {
      setStripeEnabled(false);
      setStripePublishableKey("");
      stripeInstanceRef.current = null;
      stripeInstanceKeyRef.current = "";
      return { enabled: false, publishableKey: "" };
    }

    try {
      setIsLoadingStripeConfig(true);
      const res = await fetch(`${API_URL}/api/stripe/config`, {
        credentials: "include",
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setStripeEnabled(false);
        setStripePublishableKey("");
        stripeInstanceRef.current = null;
        stripeInstanceKeyRef.current = "";
        return { enabled: false, publishableKey: "" };
      }

      const enabled = Boolean(data?.enabled && data?.publishableKey);
      const publishableKey = String(data?.publishableKey || "");

      setStripeEnabled(enabled);
      setStripePublishableKey(publishableKey);

      if (!enabled || !publishableKey) {
        stripeInstanceRef.current = null;
        stripeInstanceKeyRef.current = "";
      }

      return { enabled, publishableKey };
    } catch (e) {
      console.error("Stripe config load failed:", e);
      setStripeEnabled(false);
      setStripePublishableKey("");
      stripeInstanceRef.current = null;
      stripeInstanceKeyRef.current = "";
      return { enabled: false, publishableKey: "" };
    } finally {
      setIsLoadingStripeConfig(false);
    }
  };

  useEffect(() => {
    loadStripeConfig();
  }, [user]);

  const ensureStripeJsLoaded = async () => {
    if (window?.Stripe) return true;

    if (!stripeScriptPromiseRef.current) {
      stripeScriptPromiseRef.current = new Promise((resolve, reject) => {
        const existing = document.querySelector('script[src="https://js.stripe.com/v3/"]');
        if (existing) {
          if (window?.Stripe) {
            resolve(true);
            return;
          }
          existing.addEventListener("load", () => resolve(true), { once: true });
          existing.addEventListener("error", () => reject(new Error("Failed to load Stripe.js")), {
            once: true,
          });
          return;
        }

        const script = document.createElement("script");
        script.src = "https://js.stripe.com/v3/";
        script.async = true;
        script.onload = () => resolve(true);
        script.onerror = () => reject(new Error("Failed to load Stripe.js"));
        document.body.appendChild(script);
      });
    }

    try {
      await stripeScriptPromiseRef.current;
      return Boolean(window?.Stripe);
    } catch (e) {
      console.error(e);
      return false;
    }
  };

  const ensureStripeInstance = async () => {
    let publishableKeyToUse = String(stripePublishableKey || "");

    if (!publishableKeyToUse) {
      const cfg = await loadStripeConfig();
      if (!cfg?.enabled || !cfg?.publishableKey) return null;
      publishableKeyToUse = String(cfg.publishableKey || "");
    }

    const ok = await ensureStripeJsLoaded();
    if (!ok || !window?.Stripe || !publishableKeyToUse) return null;

    if (!stripeInstanceRef.current || stripeInstanceKeyRef.current !== publishableKeyToUse) {
      stripeInstanceRef.current = window.Stripe(publishableKeyToUse);
      stripeInstanceKeyRef.current = publishableKeyToUse;
    }

    return stripeInstanceRef.current;
  };

  useEffect(() => {
    let cancelled = false;

    const createElementStyle = {
      base: {
        color: "#222222",
        fontSize: "16px",
        fontFamily: "Arial, sans-serif",
        "::placeholder": {
          color: "#9ca3af",
        },
      },
      invalid: {
        color: "#e11d48",
      },
    };

    const handleStripeChange = (event) => {
      if (cancelled) return;
      if (event?.error?.message) setPaymentError(event.error.message);
      else setPaymentError("");
    };

    const mountCardElements = async () => {
      if (
        !paymentModalOpen ||
        !currentPaymentClientSecret ||
        !stripeCardNumberMountRef.current ||
        !stripeCardExpiryMountRef.current ||
        !stripeCardCvcMountRef.current
      ) {
        return;
      }

      const stripeInstance = await ensureStripeInstance();
      if (!stripeInstance || cancelled) {
        setPaymentError("Stripe could not be initialized.");
        return;
      }

      if (stripeCardNumberRef.current) {
        try {
          stripeCardNumberRef.current.unmount();
          stripeCardNumberRef.current.destroy();
        } catch {}
        stripeCardNumberRef.current = null;
      }

      if (stripeCardExpiryRef.current) {
        try {
          stripeCardExpiryRef.current.unmount();
          stripeCardExpiryRef.current.destroy();
        } catch {}
        stripeCardExpiryRef.current = null;
      }

      if (stripeCardCvcRef.current) {
        try {
          stripeCardCvcRef.current.unmount();
          stripeCardCvcRef.current.destroy();
        } catch {}
        stripeCardCvcRef.current = null;
      }

      stripeElementsRef.current = stripeInstance.elements();

      const cardNumber = stripeElementsRef.current.create("cardNumber", {
        style: createElementStyle,
      });
      const cardExpiry = stripeElementsRef.current.create("cardExpiry", {
        style: createElementStyle,
      });
      const cardCvc = stripeElementsRef.current.create("cardCvc", {
        style: createElementStyle,
      });

      cardNumber.mount(stripeCardNumberMountRef.current);
      cardExpiry.mount(stripeCardExpiryMountRef.current);
      cardCvc.mount(stripeCardCvcMountRef.current);

      cardNumber.on("change", handleStripeChange);
      cardExpiry.on("change", handleStripeChange);
      cardCvc.on("change", handleStripeChange);

      stripeCardNumberRef.current = cardNumber;
      stripeCardExpiryRef.current = cardExpiry;
      stripeCardCvcRef.current = cardCvc;
    };

    mountCardElements();

    return () => {
      cancelled = true;

      if (stripeCardNumberRef.current) {
        try {
          stripeCardNumberRef.current.unmount();
          stripeCardNumberRef.current.destroy();
        } catch {}
        stripeCardNumberRef.current = null;
      }

      if (stripeCardExpiryRef.current) {
        try {
          stripeCardExpiryRef.current.unmount();
          stripeCardExpiryRef.current.destroy();
        } catch {}
        stripeCardExpiryRef.current = null;
      }

      if (stripeCardCvcRef.current) {
        try {
          stripeCardCvcRef.current.unmount();
          stripeCardCvcRef.current.destroy();
        } catch {}
        stripeCardCvcRef.current = null;
      }

      stripeElementsRef.current = null;
    };
  }, [paymentModalOpen, currentPaymentClientSecret, stripePublishableKey]);

  const closePaymentModal = () => {
    if (paymentProcessing) return;

    setPaymentModalOpen(false);
    setPaymentError("");
    setPaymentSuccess("");
    setCurrentPaymentClientSecret("");
    setCurrentPaymentAmount(0);
    setCurrentPaymentBooking(null);
    setPaymentQueue([]);

    if (stripeCardNumberRef.current) {
      try {
        stripeCardNumberRef.current.unmount();
        stripeCardNumberRef.current.destroy();
      } catch {}
      stripeCardNumberRef.current = null;
    }

    if (stripeCardExpiryRef.current) {
      try {
        stripeCardExpiryRef.current.unmount();
        stripeCardExpiryRef.current.destroy();
      } catch {}
      stripeCardExpiryRef.current = null;
    }

    if (stripeCardCvcRef.current) {
      try {
        stripeCardCvcRef.current.unmount();
        stripeCardCvcRef.current.destroy();
      } catch {}
      stripeCardCvcRef.current = null;
    }

    stripeElementsRef.current = null;
  };

  const beginPaymentForBooking = async (bookingItem, queueItems = []) => {
    try {
      setPaymentError("");
      setPaymentSuccess("");

      const bookingIdValue = String(
        bookingItem?._id ||
          bookingItem?.bookingNumber ||
          bookingItem?.id ||
          ""
      ).trim();

      if (!bookingIdValue) {
        setPaymentError("Booking id is missing.");
        return false;
      }

      const res = await fetch(`${API_URL}/api/stripe/create-payment-intent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          bookingId: bookingIdValue,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data?.clientSecret) {
        setPaymentError(data?.message || "Could not start payment.");
        return false;
      }

      setCurrentPaymentBooking({
        ...bookingItem,
        _id: String(data?.bookingId || bookingItem?._id || ""),
        bookingNumber: String(data?.bookingNumber || bookingItem?.bookingNumber || ""),
      });
      setCurrentPaymentClientSecret(String(data.clientSecret || ""));
      setCurrentPaymentAmount(Number(data?.amount || bookingItem?.total || 0));
      setPaymentQueue(Array.isArray(queueItems) ? queueItems : []);
      setPaymentModalOpen(true);

      return true;
    } catch (e) {
      console.error(e);
      setPaymentError("Could not start payment.");
      return false;
    }
  };

  const startStripeCheckoutFlow = async (createdBookings) => {
    const bookings = Array.isArray(createdBookings) ? createdBookings.filter(Boolean) : [];
    if (bookings.length === 0) return false;

    const cfg =
      stripeEnabled && stripePublishableKey
        ? { enabled: stripeEnabled, publishableKey: stripePublishableKey }
        : await loadStripeConfig();

    if (!cfg?.enabled || !cfg?.publishableKey) {
      return false;
    }

    const first = bookings[0];
    const rest = bookings.slice(1);
    return beginPaymentForBooking(first, rest);
  };

  const refreshReservedDates = async () => {
    if (!propertyId) return;

    try {
      const res = await fetch(`${API_URL}/api/property/${propertyId}/reserved`);
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) return;

      const out = new Map();
      const rooms = Array.isArray(payload?.rooms) ? payload.rooms : [];
      rooms.forEach((r) => {
        const rid = Number(r?.roomId);
        const ds = (Array.isArray(r?.reservedDates) ? r.reservedDates : []).map(toISO).filter(Boolean);
        out.set(rid, ds);
      });
      setBookingsByRoomId(out);
    } catch {}
  };

  const confirmCurrentPayment = async () => {
    try {
      setPaymentProcessing(true);
      setPaymentError("");
      setPaymentSuccess("");

      if (!currentPaymentBooking || !currentPaymentClientSecret) {
        setPaymentError("Payment session is missing.");
        return;
      }

      const stripeInstance = await ensureStripeInstance();
      const cardNumber = stripeCardNumberRef.current;

      if (!stripeInstance || !cardNumber) {
        setPaymentError("Stripe card form is not ready.");
        return;
      }

      const billingDetails = {
        name: String(guestName || currentPaymentBooking?.name || "").trim(),
        email: String(guestEmail || currentPaymentBooking?.email || "").trim().toLowerCase(),
        phone: String(guestPhone || currentPaymentBooking?.phone || "").trim(),
      };

      const result = await stripeInstance.confirmCardPayment(currentPaymentClientSecret, {
        payment_method: {
          card: cardNumber,
          billing_details: billingDetails,
        },
      });

      if (result?.error) {
        setPaymentError(result.error.message || "Payment failed.");
        return;
      }

      if (!result?.paymentIntent || result.paymentIntent.status !== "succeeded") {
        setPaymentError("Payment was not completed.");
        return;
      }

      const confirmRes = await fetch(`${API_URL}/api/stripe/confirm-payment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          bookingId: String(
            currentPaymentBooking?._id ||
              currentPaymentBooking?.bookingNumber ||
              currentPaymentBooking?.id ||
              ""
          ),
        }),
      });

      const confirmData = await confirmRes.json().catch(() => ({}));

      if (!confirmRes.ok || !confirmData?.success) {
        setPaymentError(confirmData?.message || "Payment succeeded, but booking confirmation failed.");
        return;
      }

      const currentId = String(
        currentPaymentBooking?._id ||
          currentPaymentBooking?.bookingNumber ||
          currentPaymentBooking?.id ||
          ""
      );

      setPaymentSuccess(
        paymentQueue.length > 0
          ? "Payment completed. Opening next booking payment..."
          : "Payment completed successfully."
      );

      await refreshReservedDates();

      if (typeof setProperties === "function") {
        setProperties((prev) =>
          (prev || []).map((p) => {
            if (String(p?.id ?? p?._id) !== propertyId) return p;

            const nextBookings = Array.isArray(p?.bookings)
              ? p.bookings.map((b) =>
                  String(b?.id || "") === String(currentPaymentBooking?.id || "") ||
                  String(b?._id || "") === currentId
                    ? {
                        ...b,
                        status: "active",
                        bookingStatus: "active",
                        paymentStatus: "paid",
                        escrowStatus: "holding",
                        payoutStatus: "held",
                      }
                    : b
                )
              : p?.bookings;

            return {
              ...p,
              bookings: nextBookings,
            };
          })
        );
      }

      if (paymentQueue.length > 0) {
        const next = paymentQueue[0];
        const rest = paymentQueue.slice(1);

        setTimeout(async () => {
          setPaymentSuccess("");
          const ok = await beginPaymentForBooking(next, rest);
          if (!ok) {
            setPaymentError(
              "Previous payment succeeded, but the next payment could not start. You can continue later."
            );
          }
        }, 900);
      } else {
        setSelectedDatesByRoom((property?.rooms || []).map((r) => ({ roomId: r.id, dates: [] })));
        setGuestMessage("");

        setTimeout(() => {
          closePaymentModal();
          alert("Reservation and payment completed successfully.");
        }, 900);
      }
    } catch (e) {
      console.error(e);
      setPaymentError("Payment failed.");
    } finally {
      setPaymentProcessing(false);
    }
  };

  const openReserveUI = () => {
    if (!user) {
      navigate("/login");
      return;
    }
    if (isMobile) {
      setIsReserveSheetOpen(true);
      return;
    }
    const el = document.getElementById("pd-reserve");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  /* ===================== Fetch RESERVED (PUBLIC) ===================== */
  useEffect(() => {
    if (!property || !propertyId) return;

    fetch(`${API_URL}/api/property/${propertyId}/reserved`)
      .then(async (res) => {
        if (!res.ok) return null;
        return res.json();
      })
      .then((payload) => {
        const out = new Map();
        const rooms = Array.isArray(payload?.rooms) ? payload.rooms : [];
        rooms.forEach((r) => {
          const rid = Number(r?.roomId);
          const ds = (Array.isArray(r?.reservedDates) ? r.reservedDates : []).map(toISO).filter(Boolean);
          out.set(rid, ds);
        });
        setBookingsByRoomId(out);
      })
      .catch(() => setBookingsByRoomId(new Map()));
  }, [propertyId, property]);

  /* ===================== Sets per room ===================== */
  const roomSetsById = useMemo(() => {
    const map = new Map();
    const rooms = property?.rooms || [];

    rooms.forEach((room) => {
      const availableSet = new Set((room.availableDates || []).map(toISO).filter(Boolean));
      const fromRoomReserved = (room.reservedDates || []).map(toISO).filter(Boolean);
      const fromPublicReserved = bookingsByRoomId.get(Number(room.id)) || [];
      const reservedSet = new Set([...fromRoomReserved, ...fromPublicReserved]);
      map.set(Number(room.id), { availableSet, reservedSet });
    });

    return map;
  }, [property?.rooms, bookingsByRoomId]);

  const selectedSetsByRoomId = useMemo(() => {
    const map = new Map();
    (selectedDatesByRoom || []).forEach((r) => {
      const roomId = Number(r.roomId);
      const set = new Set(normalizeDateArray(r.dates || []));
      map.set(roomId, set);
    });
    return map;
  }, [selectedDatesByRoom]);

  const mapDaysForRoom = (roomId) => ({ date }) => {
    const iso = toISO(date);
    if (!iso) return {};

    const sets = roomSetsById.get(Number(roomId));
    if (!sets) return {};
    const { availableSet, reservedSet } = sets;

    const selectedSet = selectedSetsByRoomId.get(Number(roomId)) || new Set();

    if (reservedSet.has(iso)) {
      return {
        disabled: true,
        style: {
          backgroundColor: "#ffe4e6",
          color: "#9f1239",
          textDecoration: "line-through",
          borderRadius: 10,
        },
      };
    }

    if (!availableSet.has(iso)) {
      return {
        disabled: true,
        style: {
          backgroundColor: "#f3f4f6",
          color: "#9ca3af",
          textDecoration: "line-through",
          borderRadius: 10,
        },
      };
    }

    if (selectedSet.has(iso)) {
      return {
        style: {
          backgroundColor: "#ff385c",
          color: "#ffffff",
          borderRadius: 10,
          fontWeight: 900,
        },
      };
    }

    return {
      style: {
        backgroundColor: "#e0f2fe",
        color: "#075985",
        borderRadius: 10,
      },
    };
  };

  const handleRoomDatesChange = (roomId, dates) => {
    const normalizedDates = normalizeDateArray(dates);
    setSelectedDatesByRoom((prev) =>
      prev.map((r) =>
        Number(r.roomId) === Number(roomId)
          ? { ...r, dates: normalizedDates }
          : r
      )
    );
  };

  /* ===================== Pricing per room ===================== */
  const roomById = useMemo(
    () => new Map((property?.rooms || []).map((r) => [Number(r.id), r])),
    [property?.rooms]
  );

  const roomBasePrice = (room) => {
    const rp = Number(room?.price);
    if (Number.isFinite(rp) && rp > 0) return rp;
    const pp = Number(property?.price);
    return Number.isFinite(pp) ? pp : 0;
  };

  const getNightPriceForRoom = (room, dateValue) => {
    const base = roomBasePrice(room);
    const iso = toISO(dateValue);
    const monthKey = getMonthKeyFromISO(iso);

    if (!monthKey) return base;

    const monthlyPrices = room?.monthlyPrices && typeof room.monthlyPrices === "object" ? room.monthlyPrices : {};
    const custom = Number(monthlyPrices?.[monthKey]);

    if (Number.isFinite(custom) && custom > 0) return custom;
    return base;
  };

  const getSelectedRoomTotal = (roomId) => {
    const room = roomById.get(Number(roomId));
    if (!room) return 0;

    const selectedDates = selectedDatesByRoom.find((r) => Number(r.roomId) === Number(roomId))?.dates || [];
    return selectedDates.reduce((sum, dateValue) => sum + getNightPriceForRoom(room, dateValue), 0);
  };

  const nightsByRoomId = useMemo(() => {
    const map = new Map();
    (selectedDatesByRoom || []).forEach((r) => map.set(Number(r.roomId), r.dates?.length || 0));
    return map;
  }, [selectedDatesByRoom]);

  const totalNights = useMemo(() => {
    let sum = 0;
    for (const n of nightsByRoomId.values()) sum += n;
    return sum;
  }, [nightsByRoomId]);

  const total = useMemo(() => {
    let sum = 0;
    for (const room of property?.rooms || []) {
      sum += getSelectedRoomTotal(room.id);
    }
    return sum;
  }, [property?.rooms, selectedDatesByRoom, roomById]);

  /* ===================== Reserve validation ===================== */
  const nameOk = useMemo(() => String(guestName || "").trim().length >= 2, [guestName]);

  const phoneOk = useMemo(() => {
    const raw = String(guestPhone || "").trim();
    if (!raw) return false;
    const digits = raw.replace(/[^\d]/g, "");
    if (digits.length < 8) return false;
    if (/[A-Za-z]/.test(raw)) return false;
    return true;
  }, [guestPhone]);

  const emailOk = useMemo(() => {
    const value = String(guestEmail || "").trim();
    if (!value) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }, [guestEmail]);

  const guestsOk = useMemo(() => {
    const raw = String(guestGuests || "").trim();
    if (!raw) return false;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 1;
  }, [guestGuests]);

  const canReserve = useMemo(() => {
    if (!user) return false;
    if (!stripeEnabled || isLoadingStripeConfig) return false;
    if (totalNights <= 0) return false;
    if (!nameOk || !phoneOk || !emailOk || !guestsOk) return false;
    if (isReserving) return false;
    return true;
  }, [user, stripeEnabled, isLoadingStripeConfig, totalNights, nameOk, phoneOk, emailOk, guestsOk, isReserving]);

  /* ===================== Reserve ===================== */
  const reserve = async () => {
    if (!property || !propertyId || isReserving) return;

    if (!user) return alert(t("propertyDetails.alerts.loginFirst"));
    if (!stripeEnabled) return alert("Stripe payment is required to complete this reservation.");
    if (!nameOk || !phoneOk) return alert(t("propertyDetails.alerts.validNamePhone"));
    if (!emailOk) return alert("Please enter a valid email.");
    if (!guestsOk) return alert("Please enter valid guests count.");
    if (totalNights <= 0) return alert(t("propertyDetails.alerts.selectAtLeastOneDate"));

    const reservations = selectedDatesByRoom.filter((r) => (r.dates || []).length > 0);
    if (reservations.length === 0) return alert(t("propertyDetails.alerts.selectAtLeastOneDate"));

    try {
      setIsReserving(true);
      setPaymentError("");
      setPaymentSuccess("");

      const createdBookings = [];

      for (const r of reservations) {
        const datesToReserve = normalizeDateArray(r.dates || []);
        const roomTotal = getSelectedRoomTotal(r.roomId);

        const res = await fetch(`${API_URL}/api/book`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            propertyId: propertyId,
            roomId: r.roomId,
            name: String(guestName || "").trim(),
            phone: String(guestPhone || "").trim(),
            email: String(guestEmail || "").trim().toLowerCase(),
            guests: Math.max(1, Number(guestGuests || 1)),
            message: String(guestMessage || "").trim(),
            dates: datesToReserve,
            total: roomTotal,
          }),
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success) {
          alert(data.error || data.message || t("propertyDetails.alerts.reserveFailed"));
          return;
        }

        if (data?.booking) {
          createdBookings.push(data.booking);
        }

        if (data.property && typeof setProperties === "function") {
          const incomingId = String(data.property?.id ?? data.property?._id ?? "");
          setProperties((prev) =>
            (prev || []).map((p) =>
              String(p?.id ?? p?._id) === incomingId ? { ...p, ...data.property } : p
            )
          );
        }
      }

      if (isReserveSheetOpen) {
        setIsReserveSheetOpen(false);
      }

      const paymentStarted = createdBookings.length > 0 ? await startStripeCheckoutFlow(createdBookings) : false;

      if (!paymentStarted) {
        alert("Your booking was created in pending payment status, but Stripe could not be opened.");
      }
    } catch (err) {
      console.error(err);
      alert(t("propertyDetails.alerts.reserveError"));
    } finally {
      setIsReserving(false);
    }
  };

  /* ===================== Submit review ===================== */
  const submitReview = async () => {
    if (!property || !propertyId) return;

    setReviewError("");
    setReviewSuccess("");

    if (!user) {
      navigate("/login");
      return;
    }

    const numericRating = Number(reviewRating);
    const cleanComment = String(reviewComment || "").trim();

    if (!Number.isFinite(numericRating) || numericRating < 1 || numericRating > 5) {
      setReviewError("Please choose a rating from 1 to 5.");
      return;
    }

    if (cleanComment.length < 10) {
      setReviewError("Please write at least 10 characters.");
      return;
    }

    try {
      setReviewSubmitting(true);

      const res = await fetch(`${API_URL}/api/property/${propertyId}/reviews`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          rating: numericRating,
          comment: cleanComment,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setReviewError(data?.message || data?.error || "Could not submit review.");
        return;
      }

      const incomingReviews = Array.isArray(data?.reviews)
        ? data.reviews
        : data?.review
        ? [data.review, ...(reviews || [])]
        : reviews;

      let nextAverage = Number(data?.rating ?? data?.averageRating);
      let nextCount = Number(data?.reviewsCount);

      if (!Number.isFinite(nextAverage) || !Number.isFinite(nextCount)) {
        const normalized = incomingReviews
          .map((r) => Number(r?.rating || 0))
          .filter((n) => Number.isFinite(n) && n > 0);

        nextCount = normalized.length;
        nextAverage = nextCount ? normalized.reduce((a, b) => a + b, 0) / nextCount : 0;
      }

      setReviews(incomingReviews);
      setReviewAverage(nextAverage);
      setReviewsCount(nextCount);
      setReviewComment("");
      setReviewRating(5);
      setReviewSuccess("Thanks! Your review has been added.");

      if (typeof setProperties === "function") {
        setProperties((prev) =>
          (prev || []).map((p) =>
            String(p?.id ?? p?._id) === propertyId
              ? {
                  ...p,
                  ...(data?.property || {}),
                  reviews: incomingReviews,
                  rating: nextAverage,
                  reviewsCount: nextCount,
                }
              : p
          )
        );
      }
    } catch (err) {
      console.error(err);
      setReviewError("Could not submit review.");
    } finally {
      setReviewSubmitting(false);
    }
  };

  const mainImg = images[0] || "/placeholder.png";
  const sideImgs = images.slice(1, 5);
  const hasSide = sideImgs.length > 0;

  const mapQuery = encodeURIComponent(locationTextValue || (property?.title || ""));
  const displayAmenities = showAllAmenities ? selectedAmenities : selectedAmenities.slice(0, 6);

  const effectiveRating = Number(reviewAverage || property?.rating || 0);
  const effectiveReviewsCount = Number(reviewsCount || property?.reviewsCount || 0);
  const ratingLabel = effectiveRating ? `★ ${effectiveRating.toFixed(1)}` : "New";

  /* ===================== ✅ SEO ===================== */
  const canonicalUrl = useMemo(() => {
    const pid = propertyId || String(id || "");
    if (!pid) return "https://hellastay.com";
    return `https://hellastay.com/property/${encodeURIComponent(pid)}`;
  }, [propertyId, id]);

  const seoTitle = useMemo(() => {
    if (!property) return "hellastay";
    const name = titleCase(property.title);
    const loc = shortLocation(locationTextValue);
    const locPart = loc ? ` in ${loc}` : "";
    return `${name}${locPart} | hellastay`;
  }, [property, locationTextValue]);

  const seoDescription = useMemo(() => {
    if (!property) return "Discover authentic stays in Greece with direct booking on hellastay.";
    const name = titleCase(property.title);
    const loc = shortLocation(locationTextValue);
    const base = `Book ${name}${loc ? ` in ${loc}` : ""}, Greece from €${Number(minRoomPrice || 0)} per night. Direct booking on hellastay.`;
    const extra = descText ? ` ${descText}` : "";
    return clampText(base + extra, 170);
  }, [property, locationTextValue, minRoomPrice, descText]);

  const ogImage = useMemo(() => {
    const first = images?.[0] || "";
    if (!first) return "https://hellastay.com/og-cover.jpg";
    if (first.startsWith("http://") || first.startsWith("https://")) return first;
    if (first.startsWith("/")) return `https://hellastay.com${first}`;
    return `https://hellastay.com/${first}`;
  }, [images]);

  const jsonLd = useMemo(() => {
    if (!property) return null;

    const loc = shortLocation(locationTextValue);
    const data = {
      "@context": "https://schema.org",
      "@type": "LodgingBusiness",
      name: titleCase(property.title) || "hellastay property",
      url: canonicalUrl,
      image: [ogImage],
      description: seoDescription,
      address: loc
        ? {
            "@type": "PostalAddress",
            addressLocality: loc,
            addressCountry: "GR",
          }
        : { "@type": "PostalAddress", addressCountry: "GR" },
      aggregateRating:
        effectiveRating > 0 && effectiveReviewsCount > 0
          ? {
              "@type": "AggregateRating",
              ratingValue: Number(effectiveRating.toFixed(1)),
              reviewCount: effectiveReviewsCount,
            }
          : undefined,
      priceRange: minRoomPrice ? `€${Number(minRoomPrice)}` : undefined,
    };

    Object.keys(data).forEach((k) => data[k] === undefined && delete data[k]);

    return JSON.stringify(data).replace(/</g, "\\u003c");
  }, [
    property,
    locationTextValue,
    canonicalUrl,
    ogImage,
    seoDescription,
    minRoomPrice,
    effectiveRating,
    effectiveReviewsCount,
  ]);

  /* ===================== ✅ SHARE ===================== */
  const shareTitle = useMemo(() => {
    if (!property) return "hellastay";
    const name = titleCase(property.title);
    const loc = shortLocation(locationTextValue);
    return `${name}${loc ? ` · ${loc}` : ""} | hellastay`;
  }, [property, locationTextValue]);

  const shareText = useMemo(() => {
    if (!property) return "Check out this property on hellastay.";
    const loc = shortLocation(locationTextValue);
    return `Check out this property on hellastay${loc ? ` (${loc})` : ""}.`;
  }, [property, locationTextValue]);

  const copyToClipboardFallback = async (text) => {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.top = "-1000px";
      ta.style.left = "-1000px";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      return true;
    } catch {
      return false;
    }
  };

  const handleShare = async () => {
    try {
      const url = canonicalUrl || window.location.href;
      const payload = { title: shareTitle, text: shareText, url };

      if (navigator?.share) {
        await navigator.share(payload);
        return;
      }

      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        alert("Link copied!");
        return;
      }

      const ok = await copyToClipboardFallback(url);
      if (ok) alert("Link copied!");
      else alert(url);
    } catch (err) {
      console.log("Share failed:", err);
      try {
        const url = canonicalUrl || window.location.href;
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(url);
          alert("Link copied!");
          return;
        }
      } catch {}
    }
  };

  if (!property) {
    return (
      <div className="pd">
        <div className="pd__wrap">
          <h2>{t("propertyDetails.notFoundTitle")}</h2>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta property="og:site_name" content="hellastay" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={ogImage} />
        {jsonLd && <script type="application/ld+json">{jsonLd}</script>}
      </Helmet>

      <div className="pd">
        <div className="pd__wrap">
          <div className="pd__top">
            <Link to="/" className="pd__back">
              ← {t("propertyDetails.back")}
            </Link>

            <div className="pd__actions">
              <span className="pd__actionBtn">{ratingLabel}</span>

              <button
                type="button"
                className="pd__actionBtn"
                onClick={handleShare}
                title="Share"
                aria-label="Share"
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <Share2 size={16} style={{ opacity: 0.9 }} />
                  Share
                </span>
              </button>

              <button type="button" className="pd__actionBtn" onClick={openReserveUI}>
                Reserve
              </button>
            </div>
          </div>

          <h1 className="pd__title">{titleCase(property.title)}</h1>

          <div className="pd__subrow">
            <span>{shortLocation(locationTextValue)}</span>
            <span className="pd__dividerDot" />
            <a
              className="pd__link"
              href={`https://www.google.com/maps/search/?api=1&query=${mapQuery}`}
              target="_blank"
              rel="noreferrer"
            >
              Show on map
            </a>
          </div>

          <div className="pd__gallery">
            <div className={`pd__galleryGrid ${hasSide ? "" : "pd__galleryGrid--single"}`}>
              <button
                type="button"
                className="pd__gBtn"
                onClick={() => openGallery(0)}
                title={t("propertyDetails.openPhotos")}
              >
                <img className="pd__gImg" src={mainImg} alt={t("propertyDetails.images.mainAlt")} />
              </button>

              {hasSide && (
                <div className="pd__side">
                  {sideImgs.map((img, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className="pd__gBtn"
                      onClick={() => openGallery(idx + 1)}
                      title={t("propertyDetails.openPhotos")}
                    >
                      <img className="pd__gImg" src={img} alt={t("propertyDetails.images.sideAlt", { n: idx + 1 })} />
                    </button>
                  ))}
                </div>
              )}

              <button type="button" className="pd__showAll" onClick={() => openGallery(0)}>
                {t("propertyDetails.viewAllPhotos", { count: images.length })}
              </button>
            </div>
          </div>

          <div className="pd__grid">
            <div className="pd__card">
              <div className="pd__section">
                <div className="pd__priceLine">
                  <div className="pd__from">
                    From €{minRoomPrice} <span>/ night</span>
                  </div>

                  <div className="pd__pills">
                    {!!typeLabel && (
                      <span className="pd__pill">
                        <Tag size={16} style={{ opacity: 0.9 }} /> {typeLabel}
                      </span>
                    )}
                    {Number.isFinite(Number(property?.maxGuests)) && (
                      <span className="pd__pill">
                        <Users size={16} style={{ opacity: 0.9 }} /> {Number(property.maxGuests)} guests
                      </span>
                    )}
                    {Number.isFinite(Number(property?.bedrooms)) && (
                      <span className="pd__pill">
                        <BedDouble size={16} style={{ opacity: 0.9 }} /> {Number(property.bedrooms)} bedrooms
                      </span>
                    )}
                    {Number.isFinite(Number(property?.bathrooms)) && (
                      <span className="pd__pill">
                        <Bath size={16} style={{ opacity: 0.9 }} /> {Number(property.bathrooms)} bathrooms
                      </span>
                    )}
                  </div>
                </div>

                {!!descText && (
                  <div className="pd__descWrap">
                    <p className={`pd__desc ${shouldClampDesc && !isDescExpanded ? "pd__desc--clamp" : ""}`}>
                      {descText}
                    </p>

                    {shouldClampDesc && !isDescExpanded && <div className="pd__descFade" />}

                    {shouldClampDesc && (
                      <button type="button" className="pd__more" onClick={() => setIsDescExpanded((v) => !v)}>
                        {isDescExpanded ? "Show less" : "Show more"}
                      </button>
                    )}
                  </div>
                )}

                {selectedAmenities.length > 0 && (
                  <>
                    <div className="pd__subhead">Amenities</div>
                    <div className="pd__chips">
                      {displayAmenities.map((a) => (
                        <span className="pd__chip" key={a.key}>
                          <span className="pd__chipIcon">
                            <a.Icon size={16} />
                          </span>
                          <span>{a.label}</span>
                        </span>
                      ))}

                      {selectedAmenities.length > 6 && (
                        <button
                          type="button"
                          className="pd__chip pd__chipBtn"
                          onClick={() => setShowAllAmenities((v) => !v)}
                        >
                          {showAllAmenities ? "Show less" : `Show all (${selectedAmenities.length})`}
                        </button>
                      )}
                    </div>
                  </>
                )}

                {(rules || feesObj || discountsObj) && (
                  <div className="pd__acc">
                    {rules && (
                      <Accordion title="House rules" open={openRules} onToggle={() => setOpenRules((v) => !v)}>
                        <div className="pd__kv">
                          <div className="pd__kvItem">
                            <div className="pd__kvK">Pets</div>
                            <div className="pd__kvV">{rules.petsAllowed ? "Allowed" : "Not allowed"}</div>
                          </div>
                          <div className="pd__kvItem">
                            <div className="pd__kvK">Smoking</div>
                            <div className="pd__kvV">{rules.smokingAllowed ? "Allowed" : "Not allowed"}</div>
                          </div>
                          <div className="pd__kvItem">
                            <div className="pd__kvK">Check-in</div>
                            <div className="pd__kvV">
                              {String(rules.checkInFrom || "—")} – {String(rules.checkInTo || "—")}
                            </div>
                          </div>
                          <div className="pd__kvItem">
                            <div className="pd__kvK">Check-out</div>
                            <div className="pd__kvV">{String(rules.checkOutUntil || "—")}</div>
                          </div>
                        </div>
                      </Accordion>
                    )}

                    {(feesObj || discountsObj) && (
                      <Accordion title="Pricing extras" open={openExtras} onToggle={() => setOpenExtras((v) => !v)}>
                        <div className="pd__kv">
                          <div className="pd__kvItem">
                            <div className="pd__kvK">Cleaning fee</div>
                            <div className="pd__kvV">€{Number(feesObj?.cleaningFee || 0)}</div>
                          </div>
                          <div className="pd__kvItem">
                            <div className="pd__kvK">Weekly discount</div>
                            <div className="pd__kvV">{Number(discountsObj?.weeklyPercent || 0)}%</div>
                          </div>
                          <div className="pd__kvItem">
                            <div className="pd__kvK">Monthly discount</div>
                            <div className="pd__kvV">{Number(discountsObj?.monthlyPercent || 0)}%</div>
                          </div>
                        </div>
                      </Accordion>
                    )}
                  </div>
                )}
              </div>

              <div className="pd__section">
                <h3 className="pd__h2">{t("propertyDetails.roomsAvailability")}</h3>

                <div className="pd__legend">
                  <div className="pd__legendItem">
                    <span className="pd__dot" style={{ background: "#e0f2fe" }} />{" "}
                    {t("propertyDetails.legend.available")}
                  </div>
                  <div className="pd__legendItem">
                    <span className="pd__dot" style={{ background: "#ff385c" }} />{" "}
                    {t("propertyDetails.legend.selected")}
                  </div>
                  <div className="pd__legendItem">
                    <span className="pd__dot" style={{ background: "#ffe4e6" }} />{" "}
                    {t("propertyDetails.legend.reserved")}
                  </div>
                  <div className="pd__legendItem">
                    <span className="pd__dot" style={{ background: "#f3f4f6" }} />{" "}
                    {t("propertyDetails.legend.notAvailable")}
                  </div>
                </div>

                <div className="pd__rooms">
                  {(property.rooms || []).map((room) => {
                    const selectedDatesForRoomISO =
                      selectedDatesByRoom.find((r) => Number(r.roomId) === Number(room.id))?.dates || [];
                    const selectedDatesForRoom = selectedDatesForRoomISO
                      .map(isoToDate)
                      .filter(Boolean);
                    const nights = selectedDatesForRoomISO.length || 0;
                    const pricePerNight = roomBasePrice(room);
                    const subTotal = getSelectedRoomTotal(room.id);

                    return (
                      <div className="pd__room" key={room.id}>
                        <div className="pd__roomTop">
                          <h4 className="pd__roomTitle">
                            {t("propertyDetails.roomTitle", { type: room.type, id: room.id })}
                          </h4>
                          <span className="pd__roomPrice">
                            {t("propertyDetails.pricePerNight", { price: pricePerNight })}
                          </span>
                        </div>

                        <p className="pd__roomHint">{t("propertyDetails.roomHint")}</p>

                        <DatePicker
                          multiple
                          value={selectedDatesForRoom}
                          onChange={(dates) => handleRoomDatesChange(room.id, dates)}
                          format="YYYY/MM/DD"
                          className="teal"
                          minDate={new Date()}
                          mapDays={mapDaysForRoom(room.id)}
                          portal
                          zIndex={1000000}
                        />

                        <div className="pd__roomFooter">
                          <span className="pd__mini">{t("propertyDetails.selectedNights", { n: nights })}</span>
                          <span className="pd__mini">{t("propertyDetails.subtotal", { total: subTotal })}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="pd__reserve" id="pd-reserve">
              <div className="pd__reserveHead">
                <div className="pd__reservePrice">
                  €{minRoomPrice} <span>{t("propertyDetails.perNight")}</span>
                </div>
              </div>

              <div className="pd__reserveBody">
                <div className="pd__sumRow">
                  <span>{t("propertyDetails.summary.nights")}</span>
                  <span>{totalNights}</span>
                </div>
                <div className="pd__sumRow" style={{ marginTop: 10 }}>
                  <span>{t("propertyDetails.summary.total")}</span>
                  <span>€{total}</span>
                </div>
                <div className="pd__sumSub">{t("propertyDetails.summary.sub")}</div>

                {!user && <div className="pd__warn">{t("propertyDetails.warnings.mustBeLoggedIn")}</div>}
                {user && totalNights === 0 && (
                  <div className="pd__warn">{t("propertyDetails.warnings.pickAtLeastOne")}</div>
                )}
                {user && !isLoadingStripeConfig && !stripeEnabled && (
                  <div className="pd__warn">
                    Stripe payment is required to complete this reservation.
                  </div>
                )}

                <div className="pd__label">{t("propertyDetails.form.namePlaceholder")}</div>
                <input
                  className="pd__input"
                  type="text"
                  placeholder={t("propertyDetails.form.namePlaceholder")}
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                />

                <div className="pd__label">{t("propertyDetails.form.phonePlaceholder")}</div>
                <input
                  className="pd__input"
                  type="text"
                  placeholder={t("propertyDetails.form.phonePlaceholder")}
                  value={guestPhone}
                  onChange={(e) => setGuestPhone(e.target.value)}
                />

                <div className="pd__label">Email</div>
                <input
                  className="pd__input"
                  type="email"
                  placeholder="Email"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                />

                <div className="pd__label">Guests</div>
                <input
                  className="pd__input"
                  type="number"
                  min={1}
                  placeholder="Guests"
                  value={guestGuests}
                  onChange={(e) => setGuestGuests(e.target.value)}
                />

                <div className="pd__label">Message to host</div>
                <textarea
                  className="pd__input"
                  rows={4}
                  placeholder="Optional message, late check-in, parking, etc."
                  value={guestMessage}
                  onChange={(e) => setGuestMessage(e.target.value)}
                  style={{ resize: "vertical", minHeight: 110 }}
                />

                {user && !nameOk && <div className="pd__warn">{t("propertyDetails.warnings.nameMin")}</div>}
                {user && nameOk && !phoneOk && <div className="pd__warn">{t("propertyDetails.warnings.phoneValid")}</div>}
                {user && !emailOk && <div className="pd__warn">Please enter a valid email.</div>}
                {user && !guestsOk && <div className="pd__warn">Please enter valid guests count.</div>}

                <button type="button" className="pd__btn" onClick={reserve} disabled={!canReserve}>
                  {isReserving ? "Processing..." : t("propertyDetails.reserve")}
                </button>

                <div className="pd__fine">
                  Tip: Pick nights from the calendars on the left, then complete payment here.
                </div>
              </div>
            </div>
          </div>

          <div className="pd__reviews">
            <div className="pd__reviewsHead">
              <div className="pd__reviewsHeadRow">
                <div>
                  <h3 className="pd__reviewsTitle">Guest reviews</h3>
                  <div className="pd__reviewsMeta">
                    {effectiveRating > 0 ? `★ ${effectiveRating.toFixed(1)}` : "New"}{" "}
                    {effectiveReviewsCount > 0 ? `· ${effectiveReviewsCount} review(s)` : "· No reviews yet"}
                  </div>
                </div>
              </div>
            </div>

            <div className="pd__reviewsGrid">
              <div className="pd__reviewsList">
                {reviews.length === 0 ? (
                  <div className="pd__reviewsEmpty">No reviews yet. Be the first to leave one.</div>
                ) : (
                  <div className="pd__reviewsItems">
                    {reviews.map((review, idx) => {
                      const author =
                        review?.userName ||
                        review?.name ||
                        review?.authorName ||
                        review?.user?.name ||
                        "Guest";

                      const comment =
                        review?.comment ||
                        review?.text ||
                        review?.message ||
                        "";

                      const createdAt =
                        review?.createdAt ||
                        review?.date ||
                        review?.updatedAt ||
                        "";

                      const reviewStars = Math.max(1, Math.min(5, Number(review?.rating || 0) || 0));

                      return (
                        <div
                          key={String(review?._id || review?.id || idx)}
                          className="pd__reviewCard"
                        >
                          <div className="pd__reviewTop">
                            <div>
                              <div className="pd__reviewName">{author}</div>
                              <div className="pd__reviewMeta">
                                {"★".repeat(reviewStars)}
                                {"☆".repeat(5 - reviewStars)}
                                {createdAt ? ` · ${formatReviewDate(createdAt)}` : ""}
                              </div>
                            </div>
                          </div>

                          {!!comment && <p className="pd__reviewText">{comment}</p>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="pd__reviewFormWrap">
                <h4 className="pd__reviewFormTitle">Leave a review</h4>
                <div className="pd__reviewFormSub">
                  Share your experience and rate this property.
                </div>

                {!user && (
                  <div className="pd__reviewLoginNote">
                    Please log in to leave a review.
                  </div>
                )}

                <div className="pd__reviewLabel">Your rating</div>

                <div className="pd__reviewStars">
                  {[1, 2, 3, 4, 5].map((star) => {
                    const active = star <= Number(reviewRating || 0);
                    return (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setReviewRating(star)}
                        disabled={!user}
                        className={`pd__reviewStarBtn ${active ? "is-active" : ""}`}
                        aria-label={`Rate ${star} star`}
                        title={`${star} star`}
                      >
                        ★
                      </button>
                    );
                  })}
                </div>

                <div className="pd__reviewLabel">Your review</div>
                <textarea
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  disabled={!user || reviewSubmitting}
                  placeholder="Write your review here..."
                  className="pd__reviewTextarea"
                />

                {reviewError && (
                  <div className="pd__reviewMsg pd__reviewMsg--error">
                    {reviewError}
                  </div>
                )}

                {reviewSuccess && (
                  <div className="pd__reviewMsg pd__reviewMsg--success">
                    {reviewSuccess}
                  </div>
                )}

                <div className="pd__reviewSubmit">
                  <button
                    type="button"
                    onClick={submitReview}
                    disabled={!user || reviewSubmitting}
                    className="pd__btn pd__reviewBtn"
                  >
                    {reviewSubmitting ? "Submitting..." : "Submit review"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {isMobile && (
            <div className="pd__mbar">
              <div className="pd__mbarLeft">
                <div className="pd__mbarPrice">
                  €{minRoomPrice} <span>/ night</span>
                </div>
                <div className="pd__mbarSub">
                  {totalNights > 0 ? `${totalNights} night(s) · €${total}` : "Select dates"}
                </div>
              </div>

              <button
                type="button"
                className="pd__mbarBtn"
                onClick={() => {
                  if (!user) return navigate("/login");
                  setIsReserveSheetOpen(true);
                }}
              >
                Reserve
              </button>
            </div>
          )}

          {isReserveSheetOpen && (
            <div className="pd__sheetOverlay" onClick={() => setIsReserveSheetOpen(false)}>
              <div className="pd__sheet" onClick={(e) => e.stopPropagation()}>
                <div className="pd__sheetTop">
                  <div className="pd__sheetHandle" />
                  <button type="button" className="pd__sheetClose" onClick={() => setIsReserveSheetOpen(false)}>
                    ✕
                  </button>
                </div>

                <div className="pd__sheetBody">
                  <div className="pd__sheetTitle">Reserve</div>

                  <div className="pd__sumRow">
                    <span>{t("propertyDetails.summary.nights")}</span>
                    <span>{totalNights}</span>
                  </div>
                  <div className="pd__sumRow" style={{ marginTop: 10 }}>
                    <span>{t("propertyDetails.summary.total")}</span>
                    <span>€{total}</span>
                  </div>
                  <div className="pd__sumSub">{t("propertyDetails.summary.sub")}</div>

                  {totalNights === 0 && <div className="pd__warn">{t("propertyDetails.warnings.pickAtLeastOne")}</div>}
                  {!isLoadingStripeConfig && !stripeEnabled && (
                    <div className="pd__warn">
                      Stripe payment is required to complete this reservation.
                    </div>
                  )}

                  <div className="pd__label">{t("propertyDetails.form.namePlaceholder")}</div>
                  <input
                    className="pd__input"
                    type="text"
                    placeholder={t("propertyDetails.form.namePlaceholder")}
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                  />

                  <div className="pd__label">{t("propertyDetails.form.phonePlaceholder")}</div>
                  <input
                    className="pd__input"
                    type="text"
                    placeholder={t("propertyDetails.form.phonePlaceholder")}
                    value={guestPhone}
                    onChange={(e) => setGuestPhone(e.target.value)}
                  />

                  <div className="pd__label">Email</div>
                  <input
                    className="pd__input"
                    type="email"
                    placeholder="Email"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                  />

                  <div className="pd__label">Guests</div>
                  <input
                    className="pd__input"
                    type="number"
                    min={1}
                    placeholder="Guests"
                    value={guestGuests}
                    onChange={(e) => setGuestGuests(e.target.value)}
                  />

                  <div className="pd__label">Message to host</div>
                  <textarea
                    className="pd__input"
                    rows={4}
                    placeholder="Optional message, late check-in, parking, etc."
                    value={guestMessage}
                    onChange={(e) => setGuestMessage(e.target.value)}
                    style={{ resize: "vertical", minHeight: 110 }}
                  />

                  {user && !nameOk && <div className="pd__warn">{t("propertyDetails.warnings.nameMin")}</div>}
                  {user && nameOk && !phoneOk && <div className="pd__warn">{t("propertyDetails.warnings.phoneValid")}</div>}
                  {user && !emailOk && <div className="pd__warn">Please enter a valid email.</div>}
                  {user && !guestsOk && <div className="pd__warn">Please enter valid guests count.</div>}

                  <button type="button" className="pd__btn" onClick={reserve} disabled={!canReserve}>
                    {isReserving ? "Processing..." : t("propertyDetails.reserve")}
                  </button>

                  <div className="pd__fine">Tip: Pick nights from the calendars, then complete payment here.</div>
                </div>
              </div>
            </div>
          )}

          {paymentModalOpen && (
            <div
              className="pd__sheetOverlay"
              onClick={closePaymentModal}
              style={{ zIndex: 1000002 }}
            >
              <div
                className="pd__sheet"
                onClick={(e) => e.stopPropagation()}
                style={{
                  maxWidth: 560,
                  width: "min(560px, 96vw)",
                  borderRadius: 24,
                }}
              >
                <div className="pd__sheetTop">
                  <div className="pd__sheetHandle" />
                  <button
                    type="button"
                    className="pd__sheetClose"
                    onClick={closePaymentModal}
                    disabled={paymentProcessing}
                  >
                    ✕
                  </button>
                </div>

                <div className="pd__sheetBody">
                  <div className="pd__sheetTitle">Complete payment</div>

                  <div
                    style={{
                      marginBottom: 14,
                      padding: 14,
                      borderRadius: 16,
                      background: "#fafafa",
                      border: "1px solid rgba(0,0,0,0.08)",
                    }}
                  >
                    <div style={{ fontWeight: 900, color: "#222", marginBottom: 6 }}>
                      {currentPaymentBooking?.bookingNumber
                        ? `Booking ${currentPaymentBooking.bookingNumber}`
                        : "Booking payment"}
                    </div>
                    <div style={{ color: "#666", fontSize: 14 }}>
                      Total amount: <strong style={{ color: "#111" }}>€{Number(currentPaymentAmount || 0)}</strong>
                    </div>
                    {paymentQueue.length > 0 && (
                      <div style={{ color: "#666", fontSize: 13, marginTop: 6 }}>
                        Remaining payments after this: {paymentQueue.length}
                      </div>
                    )}
                  </div>

                  <div className="pd__label">Card number</div>
                  <div
                    style={{
                      border: "1px solid rgba(0,0,0,0.12)",
                      borderRadius: 14,
                      padding: "16px 14px",
                      background: "#fff",
                      minHeight: 54,
                      marginBottom: 12,
                    }}
                  >
                    <div ref={stripeCardNumberMountRef} />
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 12,
                      marginBottom: 12,
                    }}
                  >
                    <div>
                      <div className="pd__label" style={{ marginTop: 0 }}>Expiry</div>
                      <div
                        style={{
                          border: "1px solid rgba(0,0,0,0.12)",
                          borderRadius: 14,
                          padding: "16px 14px",
                          background: "#fff",
                          minHeight: 54,
                        }}
                      >
                        <div ref={stripeCardExpiryMountRef} />
                      </div>
                    </div>

                    <div>
                      <div className="pd__label" style={{ marginTop: 0 }}>CVC</div>
                      <div
                        style={{
                          border: "1px solid rgba(0,0,0,0.12)",
                          borderRadius: 14,
                          padding: "16px 14px",
                          background: "#fff",
                          minHeight: 54,
                        }}
                      >
                        <div ref={stripeCardCvcMountRef} />
                      </div>
                    </div>
                  </div>

                  {paymentError && (
                    <div className="pd__warn" style={{ marginBottom: 10 }}>
                      {paymentError}
                    </div>
                  )}

                  {paymentSuccess && (
                    <div
                      style={{
                        marginBottom: 10,
                        background: "#ecfdf5",
                        color: "#065f46",
                        border: "1px solid #a7f3d0",
                        borderRadius: 12,
                        padding: "10px 12px",
                        fontWeight: 800,
                      }}
                    >
                      {paymentSuccess}
                    </div>
                  )}

                  <button
                    type="button"
                    className="pd__btn"
                    onClick={confirmCurrentPayment}
                    disabled={paymentProcessing || !currentPaymentClientSecret}
                  >
                    {paymentProcessing ? "Processing..." : `Pay €${Number(currentPaymentAmount || 0)}`}
                  </button>

                  <div className="pd__fine">
                    Your payment is processed securely with Stripe. If you close this window before paying, the booking stays pending and the dates are not reserved.
                  </div>
                </div>
              </div>
            </div>
          )}

          {isGalleryOpen && (
            <div className="pd__lb" onClick={closeGallery}>
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: "min(980px, 96vw)",
                  background: "#111",
                  borderRadius: 18,
                  overflow: "hidden",
                  border: "1px solid rgba(255,255,255,0.15)",
                  boxShadow: "0 20px 80px rgba(0,0,0,0.45)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 12px",
                    color: "white",
                    fontWeight: 900,
                  }}
                >
                  <span>
                    {t("propertyDetails.lightbox.counter", { current: activeImgIndex + 1, total: images.length })}
                  </span>
                  <button
                    type="button"
                    onClick={closeGallery}
                    style={{
                      border: "none",
                      background: "rgba(255,255,255,0.12)",
                      color: "white",
                      padding: "8px 10px",
                      borderRadius: 12,
                      cursor: "pointer",
                      fontWeight: 900,
                    }}
                  >
                    {t("propertyDetails.actions.close")}
                  </button>
                </div>

                <div style={{ position: "relative", background: "#000" }}>
                  <img
                    src={images[activeImgIndex] || "/placeholder.png"}
                    alt={t("propertyDetails.images.activeAlt")}
                    style={{ width: "100%", maxHeight: "74vh", objectFit: "contain", display: "block" }}
                  />

                  {images.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={prevImg}
                        style={{
                          position: "absolute",
                          left: 10,
                          top: "50%",
                          transform: "translateY(-50%)",
                          border: "none",
                          background: "rgba(255,255,255,0.18)",
                          color: "white",
                          padding: "10px 12px",
                          borderRadius: 14,
                          cursor: "pointer",
                          fontWeight: 950,
                        }}
                        title={t("propertyDetails.actions.previous")}
                      >
                        ‹
                      </button>

                      <button
                        type="button"
                        onClick={nextImg}
                        style={{
                          position: "absolute",
                          right: 10,
                          top: "50%",
                          transform: "translateY(-50%)",
                          border: "none",
                          background: "rgba(255,255,255,0.18)",
                          color: "white",
                          padding: "10px 12px",
                          borderRadius: 14,
                          cursor: "pointer",
                          fontWeight: 950,
                        }}
                        title={t("propertyDetails.actions.next")}
                      >
                        ›
                      </button>
                    </>
                  )}
                </div>

                {images.length > 1 && (
                  <div style={{ display: "flex", gap: 8, overflowX: "auto", padding: 10, background: "#0b0b0b" }}>
                    {images.map((img, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setActiveImgIndex(idx)}
                        style={{
                          border: idx === activeImgIndex ? "2px solid #ff385c" : "1px solid rgba(255,255,255,0.15)",
                          padding: 0,
                          borderRadius: 12,
                          overflow: "hidden",
                          background: "transparent",
                          cursor: "pointer",
                          flex: "0 0 auto",
                        }}
                        title={t("propertyDetails.images.thumbTitle", { n: idx + 1 })}
                      >
                        <img
                          src={img || "/placeholder.png"}
                          alt={t("propertyDetails.images.thumbAlt", { n: idx + 1 })}
                          style={{ width: 92, height: 66, objectFit: "cover", display: "block" }}
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}